import type {
  InternalEligibilityResponse,
  LegacyEligibilityStatus,
} from '../../hooks/use-eligibility-verifier';
import { efileComparator } from './efile';
import { esiComparator } from './esi';
import {
  DiffRow,
  eligibilityRow,
  internalProviderString,
  legacyProviderString,
  ProviderComparator,
  ProviderProjections,
} from './types';

export { efileComparator } from './efile';
export { esiComparator } from './esi';
export * from './types';

// Registry. Adding a provider — CVS, Optum — is one sibling model file and one
// entry here. Nothing else in this feature changes, and nothing in types.ts
// learns the provider's name.
//
// Two models claiming the same wire spelling is a bug the resolver cannot
// detect: the later registration silently wins the map entry. Keep the
// spellings disjoint.
const COMPARATORS: readonly ProviderComparator[] = [
  esiComparator,
  efileComparator,
];

export type ComparisonResult =
  | {
      kind: 'compared';
      provider: string;
      rows: DiffRow[];
      context: DiffRow[];
      extraColumns: DiffRow[];
      // The final model each path projects to — what this provider's downstream
      // consumer would receive from each side.
      models: ProviderProjections;
    }
  // Both sides named a provider we have a model for, but not the same one.
  // Comparing an ESI payload against a CVS one has no meaning, and the
  // disagreement is itself the finding.
  | {
      kind: 'providerMismatch';
      legacyProvider: string;
      internalProvider: string;
    }
  // No registered model covers what the responses reported. Carries the raw
  // wire strings so the page can name them without a shared provider table.
  | {
      kind: 'noModel';
      legacyProvider?: string;
      internalProvider?: string;
    }
  // At least one side didn't return, so there is nothing to compare.
  | { kind: 'incomplete' };

export type ComparisonResolver = (
  legacy?: LegacyEligibilityStatus,
  internal?: InternalEligibilityResponse,
) => ComparisonResult;

// Factory rather than a module-level function so a new provider can be routed
// end to end in a test without registering it with the app.
export const createResolver = (
  comparators: readonly ProviderComparator[],
): ComparisonResolver => {
  const byLegacy = new Map<string, ProviderComparator>();
  const byInternal = new Map<string, ProviderComparator>();
  comparators.forEach(comparator => {
    comparator.legacyApiSources.forEach(s => byLegacy.set(s, comparator));
    comparator.internalProviders.forEach(s => byInternal.set(s, comparator));
  });

  return (legacy, internal) => {
    if (!legacy || !internal) return { kind: 'incomplete' };

    const legacyString = legacyProviderString(legacy);
    const internalString = internalProviderString(internal);
    const fromLegacy = legacyString ? byLegacy.get(legacyString) : undefined;
    const fromInternal = internalString
      ? byInternal.get(internalString)
      : undefined;

    // A side that named a provider we can't place blocks comparison outright.
    // Falling back to the other side's model here would load, say, the ESI
    // model and render confident verdicts over a payload from somewhere else.
    if ((legacyString && !fromLegacy) || (internalString && !fromInternal)) {
      return {
        kind: 'noModel',
        legacyProvider: legacyString,
        internalProvider: internalString,
      };
    }

    if (fromLegacy && fromInternal && fromLegacy !== fromInternal) {
      return {
        kind: 'providerMismatch',
        legacyProvider: fromLegacy.provider,
        internalProvider: fromInternal.provider,
      };
    }

    // One side may omit the provider field entirely; the other side's model
    // stands in, since nothing contradicts it.
    const comparator = fromLegacy ?? fromInternal;
    if (!comparator) {
      return {
        kind: 'noModel',
        legacyProvider: legacyString,
        internalProvider: internalString,
      };
    }

    return {
      kind: 'compared',
      provider: comparator.provider,
      rows: [
        eligibilityRow(legacy, internal),
        ...comparator.compare(legacy, internal),
      ],
      context: comparator.context(legacy, internal),
      extraColumns: comparator.extraColumns?.(legacy, internal) ?? [],
      models: comparator.project(legacy, internal),
    };
  };
};

export const resolveComparison: ComparisonResolver =
  createResolver(COMPARATORS);
